import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import InstallPrompt from "@/components/InstallPrompt";
import Auth from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";
import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";
import Privacy from "@/pages/Privacy";
import About from "@/pages/About";
import OwnerPanel from "@/pages/OwnerPanel";
import ResetPassword from "@/pages/ResetPassword";
import Articles from "@/pages/Articles";
import Article from "@/pages/Article";
import ResumeBuilder from "@/pages/ResumeBuilder";
import ProPage from "@/pages/ProPage";
import PhotosPage from "@/pages/PhotosPage";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f]">
        <div className="flex gap-2">
          <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
        </div>
      </div>
    );
  if (!user) return <Navigate to="/auth" replace />;
  return children;
}

function AuthGate() {
  const { user } = useAuth();
  if (user) return <Navigate to="/app" replace />;
  return <Auth />;
}

function Home() {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f]">
        <div className="flex gap-2">
          <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
        </div>
      </div>
    );
  if (user) return <Navigate to="/app" replace />;
  return <Landing />;
}

function AppRouter() {
  const location = useLocation();
  // Process the Google (fragment) / Apple (query) OAuth return BEFORE any routing.
  if (location.hash?.includes("session_id=") || location.search?.includes("apple_token=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/auth" element={<AuthGate />} />
      <Route path="/join/:token" element={<AuthGate />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/about" element={<About />} />
      <Route path="/articles" element={<Articles />} />
      <Route path="/articles/:slug" element={<Article />} />
      <Route path="/resume" element={<Protected><ResumeBuilder /></Protected>} />
      <Route path="/pro" element={<Protected><ProPage /></Protected>} />
      <Route path="/photos" element={<Protected><PhotosPage /></Protected>} />
      <Route path="/owner" element={<Protected><OwnerPanel /></Protected>} />
      <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
      <Route path="/payment/cancel" element={<Protected><PaymentCancel /></Protected>} />
      <Route path="/app/*" element={<Protected><Dashboard /></Protected>} />
      <Route path="/*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App grain">
      <AuthProvider>
        <BrowserRouter>
          <Toaster theme="dark" position="top-center" richColors />
          <InstallPrompt />
          <AppRouter />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
